import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, Observable, Subscription } from 'rxjs';
import { ProductService } from '../../product.service';
import { ProductPackagingService } from '../../product-packaging.service';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { Product, ProductPackaging } from '../../product.model';
import { MeasurementUnit, RawMaterialItem } from '../../../raw-material-inventory/raw-material.model';
import { SnackbarService } from '../../../services/snackbar.service';
import { ErrorParserService } from '../../../services/error-parser.service';
import { SnackbarAlertStatus } from '../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from '../../../common/components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-product-packaging-details',
  standalone: false,
  templateUrl: './product-packaging-details.component.html',
  styleUrl: './product-packaging-details.component.scss',
})
export class ProductPackagingDetailsComponent implements OnInit, OnDestroy {
  productId!: string;
  packagingId!: string;
  product!: Product;
  packaging!: ProductPackaging;
  isDataLoading = true;
  isSaving = false;

  rawMaterials: RawMaterialItem[] = [];
  units: MeasurementUnit[] = [];
  packagingTypes: any[] = [];

  packagingForm!: FormGroup;
  imagePreview: string | ArrayBuffer | null = null;
  selectedImageFile: File | null = null;
  errorMessage: string | null = null;
  private routeSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private productService: ProductService,
    private productPackagingService: ProductPackagingService,
    private rawMaterialService: RawMaterialService,
    private snackbarService: SnackbarService,
    private errorParserService: ErrorParserService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(params => {
      this.productId = params.get('productId')!;
      this.packagingId = params.get('packagingId')!;
      this.getInitialData();
    });
  }

  private getInitialData(): void {
    this.isDataLoading = true;
    forkJoin([
      this.rawMaterialService.list(),
      this.productService.get(this.productId),
      this.rawMaterialService.get_units(),
      this.productService.packaging_types(),
      this.productPackagingService.get(this.packagingId),
    ])
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: ([rawMaterials, product, units, packagingTypes, packaging]) => {
          this.product = product;
          this.rawMaterials = rawMaterials;
          this.units = units;
          this.packagingTypes = packagingTypes;
          this.packaging = packaging;

          if (!this.packaging) {
            console.error('Packaging not found');
            this.router.navigate(['/products', this.productId]);
            return;
          }

          this.initForm();
        },
        error: err => {
          console.error(err);
          this.router.navigate(['/products', this.productId]);
        },
      });
  }

  private initForm(): void {
    this.packagingForm = this.fb.group({
      packing_size: [this.packaging?.packing_size || '', [Validators.required]],
      packaging_type: [this.packaging?.packaging_type?.id || null],
      dealer_rate: [this.packaging?.dealer_rate || '', Validators.required],
      gst_rate: [this.packaging?.gst_rate || '', Validators.required],
      mrp: [this.packaging?.mrp || '', Validators.required],
      unit: [this.packaging?.unit?.id || '', Validators.required],
      nos: [this.packaging?.nos || '', Validators.required],
      dealer_rate_incl_gst: [{ value: this.packaging?.dealer_rate_incl_gst || '', disabled: true }],
      materials: this.fb.array(
        (this.packaging?.bill_of_material?.materials || []).map((m: any) => this.createMaterial(m))
      ),
    });

    // Add at least one material row if none exist
    if (this.materials.length === 0) {
      this.addMaterial();
    }

    // Set up auto-calculation for dealer rate including GST
    this.packagingForm.get('dealer_rate')?.valueChanges.subscribe(() => this.updateDealerRateInclGst());
    this.packagingForm.get('gst_rate')?.valueChanges.subscribe(() => this.updateDealerRateInclGst());
  }

  private updateDealerRateInclGst(): void {
    const dealerRate = parseFloat(this.packagingForm.get('dealer_rate')?.value || '0');
    const gstRate = parseFloat(this.packagingForm.get('gst_rate')?.value || '0');

    if (isNaN(dealerRate) || isNaN(gstRate)) {
      this.packagingForm.get('dealer_rate_incl_gst')?.setValue('0.00');
      return;
    }

    const result = dealerRate + (dealerRate * gstRate) / 100;
    this.packagingForm.get('dealer_rate_incl_gst')?.setValue(result.toFixed(2));
  }

  get materials(): FormArray {
    return this.packagingForm.get('materials') as FormArray;
  }

  private createMaterial(mat?: any): FormGroup {
    const materialGroup = this.fb.group({
      raw_material: [mat?.raw_material?.id || ''],
      quantity_per_unit: [mat?.quantity_per_unit || '', [Validators.min(0)]],
      unit: [{ value: mat?.raw_material?.unit?.name || '', disabled: true }],
    });

    // Autopopulate the unit when raw_material changes
    materialGroup.get('raw_material')?.valueChanges.subscribe(rawMaterialId => {
      const selected = this.rawMaterials.find(rm => rm.id === rawMaterialId);
      if (selected) {
        materialGroup.get('unit')?.setValue(selected.unit?.name || '');
      } else {
        materialGroup.get('unit')?.reset();
      }
    });

    return materialGroup;
  }

  addMaterial(): void {
    this.materials.push(this.createMaterial());
  }

  removeMaterial(index: number): void {
    this.materials.removeAt(index);
  }

  isInvalid(controlName: string): boolean {
    const control = this.packagingForm.get(controlName);
    return (control?.invalid && (control?.touched || control?.dirty)) ?? false;
  }

  onImageSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedImageFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  onRemoveImage(): void {
    this.imagePreview = null;
    this.selectedImageFile = null;

    const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
    if (fileInput) fileInput.value = '';
  }

  onSave(): void {
    if (this.packagingForm.invalid) {
      this.packagingForm.markAllAsTouched();
      return;
    }

    // Warn if no valid materials are defined
    const hasValidMaterials = this.materials.controls.some(
      ctrl => ctrl.get('raw_material')?.value && ctrl.get('quantity_per_unit')?.value
    );
    if (!hasValidMaterials) {
      const modalRef = this.modalService.open(ConfirmationModalComponent, {
        centered: true,
        backdrop: 'static',
      });
      modalRef.componentInstance.title = 'No Materials Defined';
      modalRef.componentInstance.message =
        'No bill of materials has been defined for this packaging. Are you sure you want to save without materials?';
      modalRef.componentInstance.warning = 'You can add materials later.';
      modalRef.componentInstance.confirmText = 'Save Anyway';
      modalRef.componentInstance.confirmClass = 'btn-warning';
      modalRef.componentInstance.icon = 'bi-exclamation-triangle';
      modalRef.componentInstance.iconClass = 'text-warning';

      modalRef.closed.subscribe(() => this.executeSave());
      return;
    }

    this.executeSave();
  }

  private executeSave(): void {
    this.isSaving = true;
    this.errorMessage = null;

    const formValue = this.packagingForm.value;
    const { materials, ...packagingFields } = formValue;

    // Determine what needs to be updated
    const hasImageChanged = !!this.selectedImageFile;
    const hasFormChanged = this.packagingForm.dirty || this.packagingForm.touched; // Form changed but not just image
    const hasMaterialsChanged = this.materials.dirty || this.materials.touched;

    // Build array of observables for parallel execution
    const updates: Observable<any>[] = [];

    // 1. Update image if changed
    if (hasImageChanged) {
      const imageFormData = new FormData();
      imageFormData.append('image_url', this.selectedImageFile!);

      updates.push(this.productPackagingService.patchFormData(this.packagingId, imageFormData));
    }

    // 2. Update form data if changed (excluding image)
    if (hasFormChanged || hasMaterialsChanged) {
      const payload: any = {
        ...packagingFields,
      };

      // Only include BOM if materials form array is dirty or touched
      if (hasMaterialsChanged) {
        payload.bill_of_material = {
          materials: materials.map((mat: any) => ({
            raw_material: mat.raw_material,
            quantity_per_unit: mat.quantity_per_unit,
          })),
        };
      }

      updates.push(this.productPackagingService.patch(this.packagingId, payload));
    }

    // If nothing changed, just return
    if (updates.length === 0) {
      this.isSaving = false;
      this.snackbarService.show('No changes to save', SnackbarAlertStatus.ERROR, 3000);
      return;
    }

    // Execute all updates in parallel
    forkJoin(updates)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          console.log('Saved successfully');
          this.errorMessage = null;

          // Clear the selected image preview and file
          this.imagePreview = null;
          this.selectedImageFile = null;

          // Clear the file input
          const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
          if (fileInput) fileInput.value = '';

          this.snackbarService.show('Packaging details updated successfully', SnackbarAlertStatus.SUCCESS);

          // Refresh data to get updated packaging with new image URL
          this.getInitialData();
        },
        error: err => {
          console.error('Error saving packaging details:', err);

          // Handle 500 errors with generic message
          if (err.status === 500 || err.status === 0) {
            this.errorMessage = 'Server error. Please try again later.';
          } else {
            const parsedErrors = this.errorParserService.parseError(err);
            this.errorMessage = this.errorParserService.formatErrorsAsString(parsedErrors);
          }

          this.snackbarService.show(
            this.errorMessage || 'Failed to update packaging details',
            SnackbarAlertStatus.ERROR,
            5000
          );
          // Scroll to top to show error
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
      });
  }

  onCancel(): void {
    void this.router.navigate(['/products', this.productId]);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }
}
